import { composeWrappers } from "@redotech/react-util/component";
import { OutboundViewsProvider } from "@redotech/redo-merchant-fulfillment-app/outbound-views";
import { AssignedFulfillmentServicesService } from "@redotech/redo-merchant-fulfillment-app/services/assigned-fulfillment-service";
import { LocationService } from "@redotech/redo-merchant-fulfillment-app/services/location-service";
import { ParcelService } from "@redotech/redo-merchant-fulfillment-app/services/parcel-service";
import { WorkOrderViewsProvider } from "@redotech/redo-merchant-fulfillment-app/work-orders/work-order-views";
import { InventoryViewsProvider } from "@redotech/redo-merchant-inventory-app/inventory-views";
import { OrderTrackingShipmentViewsProvider } from "@redotech/redo-merchant-marketing-app/order-tracking/shipments/order-tracking-shipment-views";
import { ConversationCountsService } from "@redotech/redo-merchant-support-app/services/conversation-counts-service";
import { ConversationTagsService } from "@redotech/redo-merchant-support-app/services/conversation-tags-service";
import { CustomerTagsService } from "@redotech/redo-merchant-support-app/services/customer-tags-services";
import { EmailIntegrationsService } from "@redotech/redo-merchant-support-app/services/email-integrations";
import { MacrosService } from "@redotech/redo-merchant-support-app/services/macros-service";
import { PhoneNumberService } from "@redotech/redo-merchant-support-app/services/phone-number-service";
import { SupportViewOrderingService } from "@redotech/redo-merchant-support-app/services/support-view-ordering-service";
import { TeamCallAvailabilitiesService } from "@redotech/redo-merchant-support-app/services/team-call-availabilities";
import { UserCallAvailabilityContextProvider } from "@redotech/redo-merchant-support-app/services/user-call-availability";
import { ViewsProvider } from "@redotech/redo-merchant-support-app/views";
import { memo, ReactElement } from "react";
import { RivoIntegrationProvider } from "../app/rivo-integration-provider";
import { ConciergeOnboardingAnalyticsProvider } from "../concierge/context/concierge-onboarding-analytics-provider";
import { FeatureOnboarding } from "../feature-onboarding";
import { IntelligentInventoryFlagProvider } from "../intelligent-inventory-flag-provider";
import { MarketingModalContextProvider } from "../redo-marketing/marketing-modal-context";
import { ProductSuiteProvider } from "../redo-suite/product-suite";
import { ClaimViewsProvider } from "./claims/claim-views";
import { ClaimViewOrderingService } from "./claims/claims-view-ordering-service";
import { TeamSwitcherService } from "./global/team-switcher-service";
import { UserProfileService } from "./global/user-profile-service";
import { DiscountsService } from "./marketing/discounts-service";
import { ReviewsToReviewService } from "./marketing/reviews-to-review-service";
import { SegmentsService } from "./marketing/segments-service";
import { OrdersReviewCountService } from "./orders/orders-review-count-service";
import { RecycleViewOrderingService } from "./recycle/recycle-view-ordering-service";
import { RecycleViewsProvider } from "./recycle/recycle-views";
import { ReturnViewsProvider } from "./returns/return-views";
import { ReturnViewOrderingService } from "./returns/returns-view-ordering-service";
import { WarrantyViewOrderingService } from "./warranties/warranty-view-ordering-service";
import { WarrantyViewsProvider } from "./warranties/warranty-views";

export const DataServices = memo(function DataServices({
  children,
}: {
  children: ReactElement;
}) {
  return composeWrappers(
    (inner) => <RivoIntegrationProvider>{inner}</RivoIntegrationProvider>,
    (inner) => <ReturnViewsProvider>{inner}</ReturnViewsProvider>,
    (inner) => <ClaimViewsProvider>{inner}</ClaimViewsProvider>,
    (inner) => <WarrantyViewsProvider>{inner}</WarrantyViewsProvider>,
    (inner) => <RecycleViewsProvider>{inner}</RecycleViewsProvider>,
    (inner) => (
      <ConciergeOnboardingAnalyticsProvider>
        {inner}
      </ConciergeOnboardingAnalyticsProvider>
    ),
    (inner) => <OutboundViewsProvider>{inner}</OutboundViewsProvider>,
    (inner) => <WorkOrderViewsProvider>{inner}</WorkOrderViewsProvider>,
    (inner) => <InventoryViewsProvider>{inner}</InventoryViewsProvider>,
    (inner) => (
      <OrderTrackingShipmentViewsProvider>
        {inner}
      </OrderTrackingShipmentViewsProvider>
    ),
    (inner) => <PhoneNumberService>{inner}</PhoneNumberService>,
    (inner) => <ConversationTagsService>{inner}</ConversationTagsService>,
    (inner) => <CustomerTagsService>{inner}</CustomerTagsService>,
    (inner) => <MacrosService>{inner}</MacrosService>,
    (inner) => (
      <UserCallAvailabilityContextProvider>
        {inner}
      </UserCallAvailabilityContextProvider>
    ),
    (inner) => (
      <TeamCallAvailabilitiesService>{inner}</TeamCallAvailabilitiesService>
    ),
    (inner) => <EmailIntegrationsService>{inner}</EmailIntegrationsService>,
    (inner) => <ParcelService>{inner}</ParcelService>,
    (inner) => <LocationService>{inner}</LocationService>,
    (inner) => (
      <AssignedFulfillmentServicesService>
        {inner}
      </AssignedFulfillmentServicesService>
    ),
    (inner) => <ProductSuiteProvider>{inner}</ProductSuiteProvider>,
    (inner) => (
      <MarketingModalContextProvider>{inner}</MarketingModalContextProvider>
    ),
    (inner) => <SegmentsService>{inner}</SegmentsService>,
    (inner) => <DiscountsService>{inner}</DiscountsService>,
    (inner) => <ViewsProvider>{inner}</ViewsProvider>,
    (inner) => <ConversationCountsService>{inner}</ConversationCountsService>,
    (inner) => <SupportViewOrderingService>{inner}</SupportViewOrderingService>,
    (inner) => <ReturnViewOrderingService>{inner}</ReturnViewOrderingService>,
    (inner) => <ClaimViewOrderingService>{inner}</ClaimViewOrderingService>,
    (inner) => (
      <WarrantyViewOrderingService>{inner}</WarrantyViewOrderingService>
    ),
    (inner) => <RecycleViewOrderingService>{inner}</RecycleViewOrderingService>,
    (inner) => <OrdersReviewCountService>{inner}</OrdersReviewCountService>,
    (inner) => <UserProfileService>{inner}</UserProfileService>,
    (inner) => <TeamSwitcherService>{inner}</TeamSwitcherService>,
    (inner) => <ReviewsToReviewService>{inner}</ReviewsToReviewService>,
    (inner) => (
      <IntelligentInventoryFlagProvider>
        {inner}
      </IntelligentInventoryFlagProvider>
    ),
    (inner) => (
      <>
        <FeatureOnboarding />
        {inner}
      </>
    ),
  )(children);
});
